import React, { useState, useMemo, useEffect } from 'react';
import { usePermission } from '../../../src/utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Progress } from '../../ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { 
  CreditCard, 
  Plus,
  Search,
  Eye,
  Download,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Users,
  Building,
  Save,
  X,
  Edit,
  Upload,
  Trash2,
  Copy,
  Archive,
  MoreVertical,
  Paperclip,
  RefreshCw,
  FileText,
  Clock,
  Send,
  Banknote
} from 'lucide-react';
import { financialService, StandalonePayment, StandalonePaymentCreateData } from '../../../src/services/financial.service';
import { procurementService } from '../../../src/services/procurement.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { usePayments } from '../../../src/hooks/queries/use-payments';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useContracts } from '../../../src/hooks/queries/use-contracts';
import { useAppNav } from '../../../src/contexts/app-nav.context';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { CURRENCIES, getCachedCurrency } from '../../../src/utils/currency';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';

// Payment Form Component
function PaymentFormModal({ mode, payment, trigger, onSuccess, orgId, projects = [], availableContracts = [] }: {
  mode: 'add' | 'edit' | 'view';
  payment?: any;
  trigger: React.ReactNode;
  onSuccess?: () => void;
  orgId?: string;
  projects?: any[];
  availableContracts?: any[];
}) {
  const isReadOnly = mode === 'view';
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    paymentNumber: payment?.paymentNumber || '',
    referenceType: payment?.referenceType || 'Invoice',
    referenceNumber: payment?.referenceNumber || '',
    paymentType: payment?.paymentType || 'Outgoing',
    paymentMethod: payment?.paymentMethod || 'ACH Transfer',
    vendor: payment?.vendor || '',
    client: payment?.client || '',
    project: payment?.project || '',
    contract: payment?.contract || '',
    paymentDate: payment?.paymentDate || new Date().toISOString().split('T')[0],
    dueDate: payment?.dueDate || '',
    amount: payment?.amount || '',
    currency: payment?.currency || getCachedCurrency(),
    description: payment?.description || '',
    bankAccount: payment?.bankAccount || '',
    checkNumber: payment?.checkNumber || '',
    confirmationNumber: payment?.confirmationNumber || '',
    exchangeRate: payment?.exchangeRate || '1.0',
    discountAmount: payment?.discountAmount || '0',
    discountDate: payment?.discountDate || '',
    lateFeeAmount: payment?.lateFeeAmount || '0',
    notes: payment?.notes || '',
    approvalRequired: payment?.approvalRequired || false,
    paymentItems: payment?.paymentItems || [] as { id: number; description: string; invoiceNumber: string; originalAmount: number; discountAmount: number; netAmount: number; dueDate: string }[],
    approvalHistory: payment?.approvalHistory || [] as { id: number; approver: string; role: string; status: string; date: string; comments: string; level: number; required: boolean }[],
    attachments: payment?.attachments || [] as { id: number; name: string; type: string; size: string; uploadDate: string; uploadedBy: string }[],
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // DB-loaded data
  const [vendors, setVendors] = useState<any[]>([]);

  useEffect(() => {
    if (!open || !orgId) return;
    procurementService.getVendors(1, 200).then((res: any) => {
      setVendors((res?.data as any)?.data ?? (res as any)?.data ?? []);
    }).catch(() => {});
  }, [open, orgId]);

  useEffect(() => {
    if (!open || mode !== 'add' || !orgId) return;
    financialService.getNextPaymentNumber(orgId).then((res: any) => {
      if (res?.data?.payment_number) {
        setFormData(prev => ({ ...prev, paymentNumber: res.data.payment_number }));
      }
    }).catch(() => {});
  }, [open, mode, orgId]);

  const paymentMethods = [
    'ACH Transfer',
    'Wire Transfer',
    'Check',
    'Credit Card',
    'Cash',
    'Electronic Payment'
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.paymentNumber.trim()) {
      newErrors.paymentNumber = 'Payment number is required';
    }
    if (!formData.referenceNumber.trim()) {
      newErrors.referenceNumber = 'Reference number is required';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valid payment amount is required';
    }
    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }
    if (formData.paymentType === 'Outgoing' && !formData.vendor) {
      newErrors.vendor = 'Vendor is required for outgoing payments';
    }
    if (formData.paymentType === 'Incoming' && !formData.client) {
      newErrors.client = 'Client is required for incoming payments';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const selectedVendor = vendors.find((v: any) => v.vendor_id === formData.vendor);
      const payload: StandalonePaymentCreateData = {
        organization_id: orgId || '',
        payment_method: formData.paymentMethod,
        payment_date: formData.paymentDate,
        amount: parseFloat(formData.amount) || 0,
        currency: formData.currency,
        payment_type: formData.paymentType,
        payment_number: formData.paymentNumber || undefined,
        vendor_name: formData.paymentType === 'Outgoing' ? (selectedVendor?.vendor_name ?? formData.vendor ?? undefined) : undefined,
        client_name: formData.paymentType === 'Incoming' ? formData.client || undefined : undefined,
        project_id: formData.project || undefined,
        contract_id: formData.contract || undefined,
        reference_number: formData.referenceNumber || undefined,
        description: formData.description || undefined,
        notes: formData.notes || undefined,
        bank_account: formData.bankAccount || undefined,
        bank_reference: formData.confirmationNumber || undefined,
        discount_amount: parseFloat(formData.discountAmount) || 0,
        late_fee_amount: parseFloat(formData.lateFeeAmount) || 0,
        approval_required: formData.approvalRequired,
        status: 'Pending',
      };
      if (payment?.payment_id) {
        await financialService.updateStandalonePayment(payment.payment_id, payload);
        showSuccess('Payment updated successfully');
      } else {
        await financialService.createStandalonePayment(payload);
        showSuccess('Payment recorded successfully');
      }
      setOpen(false);
      onSuccess?.();
    } catch {
      showError('Failed to save payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updatePaymentItem = (id: number, field: string, value: string | number) => {
    setFormData({
      ...formData,
      paymentItems: formData.paymentItems.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'originalAmount' || field === 'discountAmount') {
            updated.netAmount = updated.originalAmount - updated.discountAmount;
          }
          return updated;
        }
        return item;
      })
    });
  };

  const updateApprovalHistory = (id: number, field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      approvalHistory: formData.approvalHistory.map(approval => 
        approval.id === id ? { ...approval, [field]: value } : approval
      )
    });
  };

  const calculateTotals = () => {
    const totalOriginal = formData.paymentItems.reduce((sum, item) => sum + item.originalAmount, 0);
    const totalDiscount = formData.paymentItems.reduce((sum, item) => sum + item.discountAmount, 0);
    const totalNet = totalOriginal - totalDiscount;
    const lateFee = parseFloat(formData.lateFeeAmount) || 0;
    const finalAmount = totalNet + lateFee;
    
    return { totalOriginal, totalDiscount, totalNet, lateFee, finalAmount };
  };

  const { totalOriginal, totalDiscount, totalNet, lateFee, finalAmount } = calculateTotals();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1400px] max-h-[95vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Record - {mode === 'add' ? 'New' : mode === 'edit' ? 'Edit' : 'View'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Record a new payment transaction with complete documentation' : 
             mode === 'edit' ? 'Update payment information and approval status' : 
             'View detailed payment information and transaction history'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="items">Payment Items</TabsTrigger>
            <TabsTrigger value="banking">Banking Details</TabsTrigger>
            <TabsTrigger value="approval">Approval Process</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Information
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="payment-number">Payment Number *</Label>
                  <Input 
                    id="payment-number" 
                    value={formData.paymentNumber}
                    onChange={(e) => setFormData({...formData, paymentNumber: e.target.value})}
                    placeholder="Enter payment number"
                    readOnly={isReadOnly}
                  />
                  {errors.paymentNumber && <p className="text-sm text-destructive">{errors.paymentNumber}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment-type">Payment Type</Label>
                    <Select value={formData.paymentType} onValueChange={(value) => setFormData({...formData, paymentType: value})} disabled={isReadOnly}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Outgoing">Outgoing (Payment)</SelectItem>
                        <SelectItem value="Incoming">Incoming (Receipt)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reference-type">Reference Type</Label>
                    <Select value={formData.referenceType} onValueChange={(value) => setFormData({...formData, referenceType: value})} disabled={isReadOnly}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Invoice">Invoice</SelectItem>
                        <SelectItem value="Payment Application">Payment Application</SelectItem>
                        <SelectItem value="Change Order">Change Order</SelectItem>
                        <SelectItem value="Contract">Contract</SelectItem>
                        <SelectItem value="Expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reference-number">Reference Number *</Label>
                  <Input 
                    id="reference-number" 
                    value={formData.referenceNumber}
                    onChange={(e) => setFormData({...formData, referenceNumber: e.target.value})}
                    placeholder="Enter reference number"
                    readOnly={isReadOnly}
                  />
                  {errors.referenceNumber && <p className="text-sm text-destructive">{errors.referenceNumber}</p>}
                </div>

                {formData.paymentType === 'Outgoing' ? (
                  <div className="space-y-2">
                    <Label htmlFor="vendor">Vendor *</Label>
                    <Select value={formData.vendor} onValueChange={(value) => setFormData({...formData, vendor: value})} disabled={isReadOnly}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((v: any) => (
                          <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.vendor && <p className="text-sm text-destructive">{errors.vendor}</p>}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="client">Client *</Label>
                    <Input
                      id="client"
                      value={formData.client}
                      onChange={(e) => setFormData({...formData, client: e.target.value})}
                      placeholder="Enter client name"
                      readOnly={isReadOnly}
                    />
                    {errors.client && <p className="text-sm text-destructive">{errors.client}</p>}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="project">Project</Label>
                  <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p: any) => (
                        <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract">Contract</Label>
                  <Select value={formData.contract} onValueChange={(value) => setFormData({...formData, contract: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableContracts.map((c: any) => (
                        <SelectItem key={c.contract_id} value={c.contract_id}>
                          {c.contract_number}{c.contract_title ? ` — ${c.contract_title}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description" 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Enter payment description"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Payment Details
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment-date">Payment Date *</Label>
                    <Input 
                      id="payment-date" 
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                    {errors.paymentDate && <p className="text-sm text-destructive">{errors.paymentDate}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due-date">Due Date</Label>
                    <Input 
                      id="due-date" 
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Payment Amount *</Label>
                    <Input 
                      id="amount" 
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      placeholder="0.00"
                      readOnly={isReadOnly}
                    />
                    {errors.amount && <p className="text-sm text-destructive">{errors.amount}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})} disabled={isReadOnly}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-method">Payment Method</Label>
                  <Select value={formData.paymentMethod} onValueChange={(value) => setFormData({...formData, paymentMethod: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="discount-amount">Early Payment Discount</Label>
                    <Input 
                      id="discount-amount" 
                      type="number"
                      step="0.01"
                      value={formData.discountAmount}
                      onChange={(e) => setFormData({...formData, discountAmount: e.target.value})}
                      placeholder="0.00"
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="late-fee">Late Fee</Label>
                    <Input 
                      id="late-fee" 
                      type="number"
                      step="0.01"
                      value={formData.lateFeeAmount}
                      onChange={(e) => setFormData({...formData, lateFeeAmount: e.target.value})}
                      placeholder="0.00"
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Payment Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Gross Amount:</span>
                      <span>${totalOriginal.toLocaleString()}</span>
                    </div>
                    {totalDiscount > 0 && (
                      <div className="flex justify-between">
                        <span>Discount:</span>
                        <span>-${totalDiscount.toLocaleString()}</span>
                      </div>
                    )}
                    {lateFee > 0 && (
                      <div className="flex justify-between">
                        <span>Late Fee:</span>
                        <span>+${lateFee.toLocaleString()}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Net Payment:</span>
                      <span>${finalAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea 
                    id="notes" 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes or comments"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="items" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Payment Items
              </h3>
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Invoice/Reference</TableHead>
                    <TableHead>Original Amount</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Net Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.paymentItems.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.description}</TableCell>
                      <TableCell>{item.invoiceNumber}</TableCell>
                      <TableCell>${item.originalAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          step="0.01"
                          value={item.discountAmount}
                          onChange={(e) => updatePaymentItem(item.id, 'discountAmount', parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          readOnly={isReadOnly}
                          className="w-24"
                        />
                      </TableCell>
                      <TableCell className="font-medium">${item.netAmount.toLocaleString()}</TableCell>
                      <TableCell>{item.dueDate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="banking" className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              Banking & Transaction Details
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bank-account">Bank Account</Label>
                  <Select value={formData.bankAccount} onValueChange={(value) => setFormData({...formData, bankAccount: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="checking-001">Main Checking - ***1234</SelectItem>
                      <SelectItem value="checking-002">Project Checking - ***5678</SelectItem>
                      <SelectItem value="money-market">Money Market - ***9012</SelectItem>
                      <SelectItem value="savings-001">Business Savings - ***3456</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="check-number">Check Number</Label>
                  <Input 
                    id="check-number" 
                    value={formData.checkNumber}
                    onChange={(e) => setFormData({...formData, checkNumber: e.target.value})}
                    placeholder="Enter check number (if applicable)"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmation-number">Confirmation Number</Label>
                  <Input 
                    id="confirmation-number" 
                    value={formData.confirmationNumber}
                    onChange={(e) => setFormData({...formData, confirmationNumber: e.target.value})}
                    placeholder="Enter transaction confirmation number"
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="exchange-rate">Exchange Rate</Label>
                  <Input 
                    id="exchange-rate" 
                    type="number"
                    step="0.0001"
                    value={formData.exchangeRate}
                    onChange={(e) => setFormData({...formData, exchangeRate: e.target.value})}
                    placeholder="1.0000"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-blue-800">Transaction Information</h4>
                  <div className="space-y-1 text-sm text-blue-700">
                    <div className="flex justify-between">
                      <span>Payment Method:</span>
                      <span>{formData.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Currency:</span>
                      <span>{formData.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Amount:</span>
                      <span>{formData.currency} {parseFloat(formData.amount || '0').toLocaleString()}</span>
                    </div>
                    {formData.exchangeRate !== '1.0' && (
                      <div className="flex justify-between font-medium">
                        <span>USD Equivalent:</span>
                        <span>USD {(parseFloat(formData.amount || '0') * parseFloat(formData.exchangeRate)).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="approval" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Approval Workflow
              </h3>
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Level</TableHead>
                    <TableHead>Approver</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Comments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.approvalHistory.map(approval => (
                    <TableRow key={approval.id}>
                      <TableCell>
                        <Badge variant="outline">Level {approval.level}</Badge>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.approver}
                          onChange={(e) => updateApprovalHistory(approval.id, 'approver', e.target.value)}
                          placeholder="Approver name"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.role}
                          onChange={(e) => updateApprovalHistory(approval.id, 'role', e.target.value)}
                          placeholder="Role"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={approval.required.toString()} 
                          onValueChange={(value) => updateApprovalHistory(approval.id, 'required', value === 'true')}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Required</SelectItem>
                            <SelectItem value="false">Optional</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={approval.status} 
                          onValueChange={(value) => updateApprovalHistory(approval.id, 'status', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Approved">Approved</SelectItem>
                            <SelectItem value="Rejected">Rejected</SelectItem>
                            <SelectItem value="Under Review">Under Review</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="date"
                          value={approval.date}
                          onChange={(e) => updateApprovalHistory(approval.id, 'date', e.target.value)}
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.comments}
                          onChange={(e) => updateApprovalHistory(approval.id, 'comments', e.target.value)}
                          placeholder="Comments"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Paperclip className="h-5 w-5" />
                Supporting Documents
              </h3>
              {!isReadOnly && (
                <Button size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Upload Date</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.attachments.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.type}</Badge>
                      </TableCell>
                      <TableCell>{doc.size}</TableCell>
                      <TableCell>{doc.uploadDate}</TableCell>
                      <TableCell>{doc.uploadedBy}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          {!isReadOnly && (
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          {!isReadOnly && (
            <>
              <Button variant="outline">
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {mode === 'add' ? 'Record Payment' : 'Update Payment'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Payments() {
  const { canWrite, canApprove, canDelete } = usePermission();
  const orgCurrency = useOrgCurrency();
  const { orgId } = useAuth();
  const { routeFilters } = useAppNav();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  const { data: rawPayments = [], isLoading: loading, refetch } = usePayments(orgId);
  const { data: rawProjects = [] } = useProjects(orgId);
  const projects = rawProjects as any[];
  const { data: rawContracts = [] } = useContracts(orgId);
  const contracts = rawContracts as any[];

  // Apply incoming route filters on first render
  useEffect(() => {
    if (routeFilters.ap_invoice_id) {
      // can pre-filter by reference number if needed
    }
  }, []); // intentional empty dep

  const getProjectName = (projectId: string) => projects.find((p: any) => p.project_id === projectId)?.project_name || projectId;

  const handleApprovePayment = async (id: string) => {
    try {
      await financialService.approveStandalonePayment(id);
      showSuccess('Payment approved');
      refetch();
    } catch {
      showError('Failed to approve payment');
    }
  };

  const handleDeletePayment = async (id: string) => {
    try {
      await financialService.deleteStandalonePayment(id);
      showSuccess('Payment deleted');
      refetch();
    } catch {
      showError('Failed to delete payment');
    }
  };

  const payments = (rawPayments as StandalonePayment[]).map(p => ({
    id: p.payment_id,
    paymentNumber: p.payment_number || '',
    referenceType: 'Invoice',
    referenceNumber: p.reference_number || '',
    paymentType: p.payment_type,
    paymentMethod: p.payment_method,
    vendor: p.vendor_name || null,
    client: p.client_name || null,
    project: p.project_id || '',
    contract: p.contract_id || '',
    paymentDate: p.payment_date,
    dueDate: '',
    amount: p.amount,
    currency: p.currency,
    status: p.status,
    bankAccount: p.bank_account || '',
    confirmationNumber: p.bank_reference || '',
    description: p.description || '',
    lastUpdated: p.updated_at,
    _raw: p,
  }));

  const filteredPayments = useMemo(() => {
    return payments.filter((payment: typeof payments[0]) => {
      const matchesSearch = payment.paymentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          payment.referenceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (payment.vendor && payment.vendor.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (payment.client && payment.client.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          payment.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || payment.status.toLowerCase().replace(' ', '') === statusFilter;
      const matchesType = typeFilter === 'all' || payment.paymentType.toLowerCase() === typeFilter;
      const matchesMethod = methodFilter === 'all' || payment.paymentMethod === methodFilter;
      
      return matchesSearch && matchesStatus && matchesType && matchesMethod;
    });
  }, [searchTerm, statusFilter, typeFilter, methodFilter, payments]);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase().replace(' ', '')) {
      case 'pendingapproval':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending Approval</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Processing</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Completed</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Failed</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'Incoming' ? (
      <TrendingUp className="h-4 w-4 text-green-600" />
    ) : (
      <TrendingDown className="h-4 w-4 text-red-600" />
    );
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setMethodFilter('all');
  };

  const getTotalStats = () => {
    const pending = payments.filter((p: typeof payments[0]) => p.status === 'Pending' || p.status === 'Pending Approval').length;
    const outgoingTotal = payments.filter((p: typeof payments[0]) => p.paymentType === 'Outgoing').reduce((sum: number, p: typeof payments[0]) => sum + p.amount, 0);
    const incomingTotal = payments.filter((p: typeof payments[0]) => p.paymentType === 'Incoming').reduce((sum: number, p: typeof payments[0]) => sum + p.amount, 0);
    const netCashFlow = incomingTotal - outgoingTotal;
    return { pending, outgoingTotal, incomingTotal, netCashFlow };
  };

  const stats = getTotalStats();

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Payment Management</h2>
          <p className="text-muted-foreground">Track and process all project payments with comprehensive documentation</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite('/payments') && (
            <PaymentFormModal
              mode="add"
              orgId={orgId ?? ''}
              projects={projects}
              availableContracts={contracts}
              onSuccess={() => refetch()}
              trigger={
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Record Payment
                </Button>
              }
            />
          )}
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" />
              Outgoing Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{orgCurrency} {(stats.outgoingTotal / 1000).toFixed(0)}K</div>
            <p className="text-xs text-red-600 mt-1">Total paid out</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Incoming Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{orgCurrency} {(stats.incomingTotal / 1000).toFixed(0)}K</div>
            <p className="text-xs text-green-600 mt-1">Total received</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-blue-600" />
              Net Cash Flow
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.netCashFlow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {stats.netCashFlow >= 0 ? '+' : ''}{orgCurrency} {(stats.netCashFlow / 1000).toFixed(0)}K
            </div>
            <p className="text-xs text-blue-600 mt-1">Net position</p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Directory
          </CardTitle>
          <CardDescription>
            Comprehensive payment tracking and transaction management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment Details</TableHead>
                  <TableHead>Party & Project</TableHead>
                  <TableHead>Financial Information</TableHead>
                  <TableHead>Transaction Details</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map(payment => (
                  <TableRow key={payment.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium">{payment.paymentNumber}</p>
                        <p className="text-sm text-muted-foreground">
                          {payment.referenceType}: {payment.referenceNumber}
                        </p>
                        <p className="text-sm">{payment.description}</p>
                        <div className="flex items-center gap-2 text-xs">
                          {getTypeIcon(payment.paymentType)}
                          <Badge variant="outline" className="text-xs">{payment.paymentType}</Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-3 w-3" />
                          <span className="font-medium">
                            {payment.paymentType === 'Outgoing' ? payment.vendor : payment.client}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Building className="h-3 w-3" />
                          <span>{getProjectName(payment.project)}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {contracts.find((c: any) => c.contract_id === payment.contract)?.contract_number || payment.contract}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div>
                          <span className={`font-medium text-lg ${
                            payment.paymentType === 'Incoming' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {payment.paymentType === 'Incoming' ? '+' : '-'}${payment.amount.toLocaleString()}
                          </span>
                          <div className="text-xs text-muted-foreground">
                            {payment.currency}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>Paid: {payment.paymentDate}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>Due: {payment.dueDate}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {payment.paymentMethod}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {payment.bankAccount}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        {getStatusBadge(payment.status)}
                        {payment.confirmationNumber && (
                          <div className="text-xs text-muted-foreground">
                            Conf: {payment.confirmationNumber}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <PaymentFormModal
                            mode="view"
                            payment={payment}
                            orgId={orgId ?? ''}
                            projects={projects}
                            availableContracts={contracts}
                            trigger={
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                            }
                          />
                          {canWrite('/payments') && (
                            <PaymentFormModal
                              mode="edit"
                              payment={payment}
                              orgId={orgId ?? ''}
                              projects={projects}
                              availableContracts={contracts}
                              onSuccess={() => refetch()}
                              trigger={
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Payment
                                </DropdownMenuItem>
                              }
                            />
                          )}
                          <DropdownMenuItem>
                            <Download className="h-4 w-4 mr-2" />
                            Download Receipt
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Send className="h-4 w-4 mr-2" />
                            Send Notification
                          </DropdownMenuItem>
                          {canApprove('/payments') && (payment as any)._raw && (
                            <DropdownMenuItem onClick={() => handleApprovePayment((payment as any)._raw.payment_id)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve Payment
                            </DropdownMenuItem>
                          )}
                          {canDelete('/payments') && (payment as any)._raw && (
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDeletePayment((payment as any)._raw.payment_id)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredPayments.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No payments match your current filters</p>
                <Button variant="outline" size="sm" onClick={resetFilters} className="mt-2">
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}